import type { MeshData, SpatialAnchor } from '@manudev/model';

export type Point2 = [number, number];
export type Point3 = [number, number, number];

export interface Box {
  min: Point3;
  max: Point3;
}

// Two frames meet here and nowhere else. The site (and every MeshData) is WebGL metres with Y
// up; ifc-lite takes builder parameters in metres with Z up, measured from the storey floor.
// The hero's rotation is on the three.js groups the meshes hang in, so it never reaches a
// coordinate: site world XZ is IFC world XY.
export const ifcFromSite = (p: Point3): Point3 => [p[0], -p[2], p[1]];
export const siteFromIfc = (p: Point3): Point3 => [p[0], p[2], -p[1]];

export const toStoreyLocal = (site: Point3, elevation: number): Point3 => {
  const [x, y, z] = ifcFromSite(site);
  return [x, y, z - elevation];
};

export const toSite = (local: Point3, elevation: number): Point3 =>
  siteFromIfc([local[0], local[1], local[2] + elevation]);

// The in-store builders divide by the anchor scale themselves, so only hand-emitted STEP
// numbers (the stair) pass through here.
export const nativeLength = (anchor: SpatialAnchor, metres: number): number =>
  Number((metres / (anchor.lengthUnitScale ?? 1)).toFixed(9));

// MeshData positions are element-local; the world point is origin + position.
export function siteBox(meshes: MeshData[]): Box | null {
  const min: Point3 = [Infinity, Infinity, Infinity];
  const max: Point3 = [-Infinity, -Infinity, -Infinity];
  let seen = false;
  for (const mesh of meshes) {
    const o = mesh.origin ?? [0, 0, 0];
    for (let i = 0; i < mesh.positions.length; i += 3) {
      seen = true;
      for (let k = 0; k < 3; k++) {
        const v = o[k]! + mesh.positions[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
  }
  return seen ? { min, max } : null;
}

// A layered element comes back as several meshes under one expressId, so the boxes are
// grouped before they are measured.
export function boxesByElement(meshes: MeshData[], elevation: number): Map<number, Box> {
  const parts = new Map<number, MeshData[]>();
  for (const mesh of meshes) {
    const list = parts.get(mesh.expressId);
    if (list) list.push(mesh);
    else parts.set(mesh.expressId, [mesh]);
  }
  const out = new Map<number, Box>();
  for (const [id, group] of parts) {
    const box = storeyLocalBox(group, elevation);
    if (box) out.set(id, box);
  }
  return out;
}

export function storeyLocalBox(meshes: MeshData[], elevation: number): Box | null {
  const box = siteBox(meshes);
  if (!box) return null;
  const a = toStoreyLocal(box.min, elevation);
  const b = toStoreyLocal(box.max, elevation);
  return {
    min: [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])],
    max: [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])],
  };
}
